'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { CSSProperties, ReactNode } from 'react';

import { BRAND_TRAITS } from '@/lib/brands/traits';
import type { CMSMedia, CMSUniversalCard } from '@/lib/cms/types';
import {
  CardCountdown,
  CardRichText,
  Cta,
  type CtaProps,
  type RichTextJson,
} from '../Card/components';
import { EntryEffectWrapper } from '../EntryEffectWrapper';
import { Heading } from '../Heading';
import { MediaWrapper, type ResponsivePair } from '../MediaWrapper';
import styles from './UniversalCard.module.scss';
import { buildUniversalCardImageSizes, buildUniversalCardCoverSlot } from './universalImageSizes';
import { spacing } from './universalSpacing';

// Native video ratio fallback for video-only media: video assets carry no
// dimensions in Contentful, so a determinate box needs an assumed ratio.
const VIDEO_FALLBACK_RATIO = 16 / 9;

export interface UniversalCardProps extends Omit<
  CMSUniversalCard,
  '__typename' | 'sys' | 'internalName'
> {
  className?: string;
  /** CMS component index of the parent grid, for E2E test targeting */
  cmsIndex?: number;
  /** Position within the parent grid, for unique E2E test ids */
  cardIndex?: number;
  /** Parent grid column count — drives responsive image `sizes`. */
  gridColumnsDesktop?: number;
  gridColumnsMobile?: number;
}

function assetRatio(asset?: CMSMedia | null): number | null {
  return asset?.width && asset?.height ? asset.width / asset.height : null;
}

function CardCta({
  cta,
  heading,
  testIdPrefix,
}: {
  cta: NonNullable<CMSUniversalCard['cta1']>;
  heading?: string | null;
  testIdPrefix?: string;
}) {
  return (
    <Cta
      urlText={cta.text}
      url={cta.url}
      urlType={cta.variant}
      urlEventType={cta.eventType}
      urlTrigger={(cta.eventTrigger?.type ?? undefined) as CtaProps['urlTrigger']}
      openUrlInNewTab={cta.target === '_blank'}
      trackingTitle={heading || ''}
      testIdPrefix={testIdPrefix}
    />
  );
}

/**
 * Universal Card — new-generation CMS card (BEC Universal components).
 * Layout options (type, paddings, alignments, media heights) live directly on
 * the card; heading/CTA/media/typography come from reused child entries.
 */
export function UniversalCard({
  type,
  subtitleTop,
  subtitleBottom,
  heading,
  headingTag,
  headingType,
  headingColor,
  headingHighlightColor,
  headingVariation,
  headingTextAlignment,
  richText,
  cta1,
  cta2,
  ctaLayout,
  image,
  mobileImage,
  video,
  mobileVideo,
  videoPlayOption,
  mediaHeightDesktop,
  mediaHeightMobile,
  textColor,
  textSize,
  textAlignment,
  textMaxWidth,
  backgroundColor,
  paddingTopBottom,
  paddingLeftRight,
  marginBottom,
  contentHorizontalAlignment,
  contentVerticalAlignment,
  deviceDisplay,
  showMoreOn,
  entryEffectType,
  countdownTo,
  anchorId,
  className,
  cmsIndex,
  cardIndex,
  gridColumnsDesktop,
  gridColumnsMobile,
}: Readonly<UniversalCardProps>) {
  const t = useTranslations('countdown');

  if (deviceDisplay === 'none') return null;

  const cardType = type || 'Text';
  const hasMedia = Boolean(image || video) && cardType !== 'Text';
  const media: ResponsivePair = { image, mobileImage, video, mobileVideo };
  // Block positioning and text alignment are independent concerns: the card's
  // alignment fields place the child blocks; the typography's textAlignment
  // aligns the text and the CTA row with it.
  const hAlign = (contentHorizontalAlignment || '').toLowerCase();
  const vAlign = (contentVerticalAlignment || '').toLowerCase();
  const textAlign = (textAlignment || '').toLowerCase();
  const hasFixedMediaHeight = mediaHeightDesktop != null || mediaHeightMobile != null;

  // Natural aspect ratios size the media box at breakpoints without a fixed
  // height ("Auto — asset driven" per the spec); video-only media assumes 16:9.
  const videoRatio = video ? VIDEO_FALLBACK_RATIO : null;
  const ratioDesktop = assetRatio(image) ?? videoRatio;
  const ratioMobile = assetRatio(mobileImage) ?? ratioDesktop;
  // Cover-fill whenever the media box has a determinate size so videos —
  // whose assets carry no dimensions — get the same box as neighboring images.
  const fillMedia =
    hasFixedMediaHeight ||
    ratioDesktop != null ||
    cardType === 'MediaBackground' ||
    cardType === 'MediaLeft' ||
    cardType === 'MediaRight';

  const testIdPrefix =
    cmsIndex !== undefined
      ? `universal-card-${cmsIndex}${cardIndex !== undefined ? `-${cardIndex}` : ''}`
      : undefined;
  const imageSizesInput = {
    cardType,
    textMaxWidth,
    columnDesktop: gridColumnsDesktop,
    columnMobile: gridColumnsMobile,
    mediaHeightDesktop,
    mediaHeightMobile,
    ratioDesktop,
    ratioMobile,
  };
  const imageSizes = buildUniversalCardImageSizes(imageSizesInput);
  const coverSlot = buildUniversalCardCoverSlot(imageSizesInput);

  const styleVars = {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(textColor ? { color: textColor } : {}),
    ...(mediaHeightDesktop != null ? { '--uc-media-h-desktop': `${mediaHeightDesktop}px` } : {}),
    ...(mediaHeightMobile != null ? { '--uc-media-h-mobile': `${mediaHeightMobile}px` } : {}),
    ...(ratioDesktop ? { '--uc-media-ratio': ratioDesktop } : {}),
    ...(ratioMobile ? { '--uc-media-ratio-mobile': ratioMobile } : {}),
  } as CSSProperties;

  const textSizeClass = textSize && textSize !== 'default' && styles[`textSize-${textSize}`];
  const renderRichText = (enableShowMore: boolean, visibilityClass?: string): ReactNode => (
    <CardRichText
      text={richText as RichTextJson}
      textColor={textColor}
      enableShowMore={enableShowMore}
      className={clsx(styles.text, textSizeClass, visibilityClass)}
    />
  );

  const card = (
    <article
      id={anchorId || undefined}
      className={clsx(
        styles.card,
        styles[`type-${cardType}`],
        spacing(paddingTopBottom) && styles[`ptb-${spacing(paddingTopBottom)}`],
        spacing(paddingLeftRight) && styles[`plr-${spacing(paddingLeftRight)}`],
        spacing(marginBottom) && styles[`mb-${spacing(marginBottom)}`],
        hAlign && styles[`hAlign-${hAlign}`],
        vAlign && styles[`vAlign-${vAlign}`],
        textAlign && styles[`textAlign-${textAlign}`],
        textMaxWidth && styles[`textWidth-${textMaxWidth}`],
        deviceDisplay === 'Desktop' && styles.hideMobile,
        deviceDisplay === 'Mobile' && styles.hideDesktop,
        className
      )}
      style={styleVars}
      data-testid={testIdPrefix}
    >
      {hasMedia && (
        <div className={styles.media}>
          <MediaWrapper
            media={media}
            fill={fillMedia}
            deferVideoLoading={BRAND_TRAITS.cmsVideo.deferOffscreenLoading}
            disableLink
            sizes={imageSizes}
            coverSlot={coverSlot}
            videoPlayOption={videoPlayOption}
            showVideoControls={
              videoPlayOption === 'PlayOnClick' || videoPlayOption === 'AutoplayLoopControls'
            }
          />
        </div>
      )}
      <div className={styles.content}>
        {subtitleTop && <p className={styles.subtitleTop}>{subtitleTop}</p>}
        {heading && (
          <Heading
            className={styles.heading}
            Heading={{
              text: heading,
              headingType,
              headingSeoTag: headingTag,
              mainColor: headingColor || textColor,
              highlightColor: headingHighlightColor,
              headingVariation,
              textAlignment: headingTextAlignment || textAlign || 'left',
            }}
          />
        )}
        {subtitleBottom && <p className={styles.subtitleBottom}>{subtitleBottom}</p>}
        {countdownTo && (
          <CardCountdown
            countdownToDatetime={countdownTo}
            textColor={textColor}
            labels={{
              days: t('days'),
              hours: t('hours'),
              minutes: t('minutes'),
              seconds: t('seconds'),
            }}
          />
        )}
        {richText?.json != null &&
          (showMoreOn === 'Desktop' ? (
            <>
              {renderRichText(true, styles.hideMobile)}
              {renderRichText(false, styles.hideDesktop)}
            </>
          ) : showMoreOn === 'Mobile' ? (
            <>
              {renderRichText(true, styles.hideDesktop)}
              {renderRichText(false, styles.hideMobile)}
            </>
          ) : (
            renderRichText(showMoreOn === 'All')
          ))}
        {(cta1 || cta2) && (
          <div className={clsx(styles.ctas, ctaLayout === 'rows' && styles.ctasRows)}>
            {cta1 && <CardCta cta={cta1} heading={heading} testIdPrefix={testIdPrefix} />}
            {cta2 && <CardCta cta={cta2} heading={heading} testIdPrefix={testIdPrefix} />}
          </div>
        )}
      </div>
    </article>
  );

  if (entryEffectType === 'Fade') {
    return (
      <EntryEffectWrapper enabled variant="fade">
        {card}
      </EntryEffectWrapper>
    );
  }
  return card;
}

UniversalCard.displayName = 'UniversalCard';
